/* Guide requests and checked marketing subscriptions use separate endpoints. */
(function (root, factory) {
  'use strict';
  var api = factory(root);
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  if (root.BNGuideClientRequest) return;
  root.BNGuideClientRequest = api;
  api.mount();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', api.mount, { once: true });
  document.addEventListener('shopify:section:load', api.mount);
})(typeof window === 'object' ? window : globalThis, function (root) {
  'use strict';
  var routes = Object.freeze({
    eu: Object.freeze({ en: 'https://eu.bionobo.com/pages/guide-library', es: 'https://eu.bionobo.com/es/pages/guide-library' }),
    es: Object.freeze({ es: 'https://bionobo.com/pages/biblioteca-de-guias', en: 'https://bionobo.com/en/pages/biblioteca-de-guias' })
  });
  var revision = '2026-07-15';
  function createClient(options) {
    options = options || {};
    var fetcher = options.fetch || root.fetch.bind(root);
    var uuid = options.uuid || function () { return root.crypto.randomUUID(); };
    var wait = options.wait || function (ms) { return new Promise(function (resolve) { root.setTimeout(resolve, ms); }); };
    function createRequest(config, email, marketing) {
      var library = routes[config.store] && routes[config.store][config.locale];
      if (!library) throw new Error('invalid-route');
      if (typeof config.guideId !== 'string' || !/^[1-9][0-9]{0,19}$/.test(config.guideId)) throw new Error('invalid-guide');
      if (!/^[A-Za-z0-9]{6}$/.test(config.siteId || '')) throw new Error('invalid-site');
      if (typeof email !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) throw new Error('invalid-email');
      if (typeof marketing !== 'boolean') throw new Error('invalid-consent');
      if (marketing && !/^[A-Za-z0-9]{6}$/.test(config.marketingListId || '')) throw new Error('invalid-list');
      var id = uuid();
      if (!/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(id)) throw new Error('invalid-request-id');
      var properties = { request_id: id, guide_id: config.guideId, locale: config.locale, source: 'bn-guide-signup', store: config.store, library_url: library };
      return {
        id: id, email: email.trim(), marketing: marketing, siteId: config.siteId, marketingListId: config.marketingListId,
        properties: properties, libraryUrl: library,
        destination: library + '?guide=' + encodeURIComponent(config.guideId) + '&lang=' + config.locale + '#selected-guide',
        body: JSON.stringify({ data: { type: 'event', attributes: {
          unique_id: id, properties: properties,
          metric: { data: { type: 'metric', attributes: { name: 'Bionobo Guide Requested' } } },
          profile: { data: { type: 'profile', attributes: { email: email.trim(), properties: { bn_guide_delivery_adapter: 'v1' } } } }
        } } }), accepted: false, flight: null, subscription: null
      };
    }
    async function post(path, site, body, keepalive) {
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, options.timeoutMs || 8000);
      try {
        return await fetcher('https://a.klaviyo.com/client/' + path + '?company_id=' + encodeURIComponent(site), {
          method: 'POST', mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer',
          headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: revision },
          body: body, signal: controller.signal, keepalive: !!keepalive
        });
      } finally { clearTimeout(timer); }
    }
    async function deliver(request) {
      var status = 0;
      for (var attempt = 0; attempt < 3; attempt++) {
        var response;
        try { response = await post('events', request.siteId, request.body, false); status = response.status; }
        catch (_) { status = 0; }
        if (status === 202) { request.accepted = true; return { accepted: true, status: status, requestId: request.id }; }
        if (status && status !== 429 && status < 500) break;
        if (attempt < 2) {
          var retryAfter = response && Number(response.headers.get('Retry-After'));
          if (retryAfter > 10) break;
          await wait(retryAfter > 0 ? retryAfter * 1000 : 500 * Math.pow(2, attempt));
        }
      }
      return { accepted: false, status: status, requestId: request.id };
    }
    function submit(request) {
      if (request.accepted) return Promise.resolve({ accepted: true, status: 202, requestId: request.id });
      if (request.flight) return request.flight;
      request.flight = deliver(request).finally(function () { request.flight = null; });
      return request.flight;
    }
    function subscribe(request) {
      if (request.marketing !== true) return Promise.resolve('not-requested');
      if (!request.accepted) return Promise.resolve('not-accepted');
      if (request.subscription) return request.subscription;
      var body = JSON.stringify({ data: { type: 'subscription', attributes: {
        custom_source: 'bn-guide-signup', profile: { data: { type: 'profile', attributes: {
          email: request.email, properties: { bn_guide_delivery_adapter: 'v1' }, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
        } } }
      }, relationships: { list: { data: { type: 'list', id: request.marketingListId } } } } });
      request.subscription = post('subscriptions', request.siteId, body, true).then(function (response) {
        return response.status === 202 ? 'accepted' : 'failed';
      }, function () { return 'failed'; });
      return request.subscription;
    }
    return { createRequest: createRequest, submit: submit, subscribe: subscribe };
  }
  function mount() {
    if (!root.document || !root.crypto || typeof root.crypto.randomUUID !== 'function') return;
    root.document.querySelectorAll('form[data-bn-guide-client-form]').forEach(function (form) {
      if (form.dataset.bnGuideMounted || form.dataset.bnGuideDeliveryReady !== 'true') return;
      var config = {store: form.dataset.store, locale: form.dataset.locale, guideId: form.dataset.guideId, siteId: form.dataset.siteId, marketingListId: form.dataset.marketingListId};
      if (!routes[config.store] || !routes[config.store][config.locale] || !/^[A-Za-z0-9]{6}$/.test(config.siteId)) return;
      var email = form.querySelector('[data-guide-email]');
      var marketing = form.querySelector('[data-guide-marketing]');
      var button = form.querySelector('button[type="submit"]');
      var status = form.querySelector('[role="status"]');
      var link = form.querySelector('[data-guide-library]');
      if (!email || !marketing || !button || !status || !link) return;
      var client = createClient(), pending, signature, busy = false, consentConfirmed = false;
      var spanish = config.locale === 'es';
      form.dataset.bnGuideMounted = 'true';
      var listReady = /^[A-Za-z0-9]{6}$/.test(config.marketingListId || '');
      email.disabled = !listReady; button.disabled = true; marketing.disabled = !listReady;
      marketing.addEventListener('change', function (event) {
        if (event.isTrusted) consentConfirmed = marketing.checked === true;
        else if (!marketing.checked) consentConfirmed = false;
        button.disabled = busy || !listReady || !consentConfirmed || !marketing.checked;
      });
      if (!listReady) status.textContent = spanish ? 'Las solicitudes por email aún no están disponibles.' : 'Email requests are not available yet.';
      form.addEventListener('submit', async function (event) {
        event.preventDefault();
        if (busy || !listReady || !consentConfirmed || !marketing.checked || !form.reportValidity()) return;
        var checked = !marketing.disabled && marketing.checked === true;
        var nextSignature = email.value.trim() + '\n' + checked;
        try {
          if (!pending || pending.accepted || signature !== nextSignature) {
            pending = client.createRequest(config, email.value, checked); signature = nextSignature;
          }
        } catch (_) {
          status.textContent = spanish ? 'Comprueba tu email y vuelve a intentarlo.' : 'Check your email and try again.';return;
        }
        busy = true; button.disabled = true; email.disabled = true; marketing.disabled = true;
        status.textContent = spanish ? 'Enviando tu solicitud…' : 'Sending your request…';
        var result = await client.submit(pending);
        busy = false; email.disabled = false; marketing.disabled = !listReady; button.disabled = !consentConfirmed || !marketing.checked;
        if (!result.accepted) {
          status.textContent = spanish ? 'No hemos podido confirmar tu solicitud. Vuelve a intentarlo.' : 'We could not confirm your request. Please try again.';
          status.focus();return;
        }
        button.disabled = true;
        status.textContent = spanish ? 'Solicitud aceptada. Abriendo la biblioteca…' : 'Request accepted. Opening the library…';
        link.href = pending.destination;link.hidden = false;
        var accepted = pending;
        client.subscribe(accepted).then(function (state) {
          if (state === 'failed') status.textContent = spanish ? 'Solicitud de guía aceptada. La suscripción a novedades no se ha confirmado.' : 'Guide request accepted. Marketing signup was not confirmed.';
        });
        var reporting;
        try {
          if (root.BN_ANALYTICS && typeof root.BN_ANALYTICS.guideSignup === 'function') reporting = root.BN_ANALYTICS.guideSignup(Object.assign({}, accepted.properties, {language: config.locale, marketing_opt_in_requested: checked}), accepted.email);
        } catch (_) {}
        var navigated = false;
        function navigate() {if (navigated) return;navigated = true;root.location.assign(accepted.destination);}
        var deadline = root.setTimeout(navigate, 1000);
        Promise.resolve(reporting).then(function () {root.clearTimeout(deadline);navigate();}, function () {root.clearTimeout(deadline);navigate();});
      });
    });
  }
  return { createClient: createClient, mount: mount, revision: revision };
});
